<template>
  <div class="bm-rules" data-testid="batching-rules">
    <button
      type="button"
      class="bm-rules__header"
      data-testid="batching-rules-toggle"
      :aria-expanded="String(open)"
      @click="open = !open"
    >
      <i class="material-icons bm-rules__gear" aria-hidden="true">tune</i>
      <span class="bm-rules__heading">
        <span class="bm-rules__title">Batching rules for this store</span>
        <span class="bm-rules__summary" data-testid="batching-rules-summary">{{ summaryLine }}</span>
      </span>
      <i class="material-icons bm-rules__chev" aria-hidden="true">{{ open ? 'expand_less' : 'expand_more' }}</i>
    </button>

    <div v-if="open" class="bm-rules__body">
      <!--
        The PROSE direction. Locked Decisions: "Recommendation: prose. It is
        the only version that can't be read wrongly, and the fields version's
        summary block is doing the same work with twice the surface." The
        labelled-fields variant is deliberately NOT built.
      -->
      <!--
        THE SENTENCE MUST PARSE IN ALL FOUR ON/OFF COMBINATIONS. The panel is
        prose, so an off state cannot just grey a field out:

          Orders collect [48 hours v] <anchor>, or until [$201] of blank goods
                                                  -- whichever comes first.
          Orders collect [forever v], or until [$201] of blank goods.
          Orders collect [48 hours v] <anchor>.
          Orders collect [forever v].

        Note what the v-ifs below are protecting against: with the threshold
        off, "of blank goods" has no subject and reads as a dangling fragment;
        with the window off, the anchor ("from the first order in a batch")
        modifies nothing; and "whichever comes first" is a claim about a RACE
        between two rules, so it is false rather than merely redundant as soon
        as one of them cannot fire. Pinned in
        __tests__/batching-rules-disableable.spec.js.

        WHERE THE THRESHOLD'S OFF SWITCH LIVES, and why it is not in the
        sentence. The window's off switch can be an option on the window
        select, because that select already carries the window's VALUE. The
        threshold's value is a free-text input, so "no limit" cannot be one of
        its values -- it needs a control of its own. Putting that control
        inline would make "or until [a blanks limit of v] [$201] of blank
        goods" read as two competing controls for one number, and with the
        clause removed entirely there would be nowhere for it to sit, so
        turning the rule back on would be impossible. It therefore goes on its
        own line below, where it is reachable in all four states.
      -->
      <p class="bm-rules__prose" data-testid="rules-prose">
        Orders collect
        <select
          class="bm-inline-select"
          data-testid="set-window"
          :value="windowSelectValue"
          @change="onWindowChange"
        >
          <option value="24">24 hours</option>
          <option value="48">48 hours</option>
          <option value="forever">forever</option>
        </select>
        <!-- Store-type aware: the window anchors differently for a pop-up.
             Meaningless with no window at all, so it goes with it. -->
        <span v-if="rules.timeRuleEnabled">{{ windowAnchorText }}</span
        ><template v-if="rules.dollarRuleEnabled"
          >, or until
          <span class="bm-inline-number">
            $<input
              class="bm-inline-number__input"
              data-testid="set-threshold"
              inputmode="numeric"
              :value="rules.threshold"
              :style="{ width: thresholdWidth }"
              @input="onThresholdInput"
            />
          </span>
          of blank goods<span v-if="rules.timeRuleEnabled">
            &mdash; whichever comes first</span
          ></template
        >. Then
        <select
          class="bm-inline-select"
          data-testid="set-mode"
          :value="rules.releaseMode"
          @change="emitChange({ releaseMode: $event.target.value })"
        >
          <option value="manual">I release it myself</option>
          <option value="automatic">it releases automatically</option>
        </select>.
      </p>

      <!-- Always rendered: it is the only way back from "no blanks limit". -->
      <p class="bm-rules__aside">
        <select
          class="bm-inline-select"
          data-testid="set-threshold-mode"
          :value="rules.dollarRuleEnabled ? 'on' : 'off'"
          @change="onThresholdModeChange"
        >
          <option value="on">Use a blanks-goods limit</option>
          <option value="off">No blanks-goods limit</option>
        </select>
      </p>

      <!--
        NO WARNING BANNER HERE, DELIBERATELY. A batch with both rules off
        never leaves `open`, so it never reaches `holding` and neither
        `batch_nag` nor `batch_release_approaching` ever claims it (both are
        gated on `state = 'holding'`). That consequence is real, but it is
        OPT-IN: a distributor has to choose "forever", and for the
        AdventHealth store it is the DESIRED behaviour.

        Jake, 2026-09-24: "The default is to auto-release, so someone would
        have had to deliberately set batches to stay open indefinitely. That's
        what we actually want for the AH store, which is why I went there."

        So no banner, no confirmation dialog, no "are you sure". Adding
        friction to a setting he deliberately wants would be inventing a
        requirement. The summary line already says "never closes
        automatically", which is the honest statement of what is configured
        without editorialising about it.
      -->
      <div class="bm-rules__toggle-row">
        <button
          type="button"
          role="switch"
          class="bm-switch"
          :class="{ 'bm-switch--on': rules.shopperEditing }"
          data-testid="set-shopper-editing"
          :aria-checked="String(rules.shopperEditing)"
          @click="emitChange({ shopperEditing: !rules.shopperEditing })"
        >
          <span class="bm-switch__knob" />
        </button>
        <div class="bm-rules__toggle-copy">
          <div class="bm-rules__toggle-label">Let shoppers fix their own orders before release</div>
          <div class="bm-rules__toggle-sub">
            Off by default. Shoppers can change style, color, size and quantity &mdash; never price.
          </div>
        </div>
      </div>

      <!-- The threshold explains itself ONCE. Gone with the rule: it asserts
           that a SPECIFIC number is the free-freight line, which is not a
           statement to make about a threshold that no longer governs
           anything. -->
      <div
        v-if="rules.dollarRuleEnabled"
        class="bm-rules__explainer"
        data-testid="threshold-explainer"
      >
        <i class="material-icons bm-rules__info" aria-hidden="true">info_outline</i>
        <span>
          {{ money(rules.threshold) }} is the supplier's free-freight line. Below it, you pay freight out of your margin.
        </span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * "Batching rules for this store" -- the collapsed panel at the top of the
 * Batches tab, "because the rules explain the list".
 *
 * Collapsed by default, with the current rules summarised in one line so the
 * distributor can read the governing rules without expanding anything.
 *
 * Only the PROSE copy direction is built. The labelled-fields variant from
 * the prototype was explicitly not recommended, and building both would put
 * two mechanisms behind one concept (design-philosophy: one mechanism per
 * concept).
 */
import { money, DEFAULT_RULES } from "@/services/batchManagementService";

export default {
  name: "BatchingRulesPanel",
  props: {
    rules: {
      type: Object,
      default: () => ({ ...DEFAULT_RULES }),
    },
  },
  emits: ["update:rules"],
  data() {
    return { open: false };
  },
  computed: {
    summaryLine() {
      // The COLLAPSED header is the one line a distributor reads without
      // expanding anything, so every clause here has to be TRUE of the
      // current rules. "48h window" on a store whose time rule is off is a
      // false statement in the most-read place on the panel.
      const r = this.rules;
      const mode = r.releaseMode === "manual" ? "manual release" : "automatic release";
      const shopper = `shopper editing ${r.shopperEditing ? "on" : "off"}`;
      if (!r.timeRuleEnabled && !r.dollarRuleEnabled) {
        return `never closes automatically · ${mode} · ${shopper}`;
      }
      const window = r.timeRuleEnabled
        ? `${r.windowHours}h window`
        : "no time limit";
      const threshold = r.dollarRuleEnabled
        ? `${money(r.threshold)} threshold`
        : "no blanks limit";
      return `${window} · ${threshold} · ${mode} · ${shopper}`;
    },
    windowSelectValue() {
      // One control, two concepts -- deliberately, because "forever" IS a
      // window length in the sentence and a distributor reads it as one. The
      // hours value is kept untouched underneath (see `onWindowChange`), so
      // switching away and back restores what they had rather than a default.
      return this.rules.timeRuleEnabled
        ? String(this.rules.windowHours)
        : "forever";
    },
    windowAnchorText() {
      // Time-window copy is store-type aware.
      return this.rules.storeType === "popup"
        ? " after this store closes"
        : " from the first order in a batch";
    },
    thresholdWidth() {
      return `${Math.max(3, String(this.rules.threshold).length + 0.5)}ch`;
    },
  },
  methods: {
    money,
    emitChange(patch) {
      this.$emit("update:rules", { ...this.rules, ...patch });
    },
    onWindowChange(e) {
      const value = e.target.value;
      if (value === "forever") {
        // ONLY the flag moves. `windowHours` is left exactly as it was, so a
        // distributor who switches back gets their own 24 or 48 rather than a
        // platform default -- and the column keeps meaning only the value,
        // which is the whole reason this is a boolean and not a sentinel.
        this.emitChange({ timeRuleEnabled: false });
        return;
      }
      this.emitChange({
        timeRuleEnabled: true,
        windowHours: Number(value),
      });
    },
    onThresholdModeChange(e) {
      // Same shape as the window: the flag moves, the threshold does not.
      this.emitChange({ dollarRuleEnabled: e.target.value === "on" });
    },
    onThresholdInput(e) {
      // Digits only. An empty field becomes 0 rather than NaN, so the
      // summary line and the threshold bar never render "$NaN".
      const digits = String(e.target.value).replace(/[^0-9]/g, "");
      this.emitChange({ threshold: Number(digits || 0) });
    },
  },
};
</script>

<style scoped>
.bm-rules {
  background: var(--tf-surface);
  border: 1px solid var(--tf-border);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 18px;
}

.bm-rules__header {
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 20px;
  min-height: 44px;
}

.bm-rules__gear,
.bm-rules__chev {
  color: var(--tf-fg-4);
  flex-shrink: 0;
}

.bm-rules__heading {
  flex: 1;
  min-width: 0;
}

.bm-rules__title {
  display: block;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--tf-fg-1);
}

.bm-rules__summary {
  display: block;
  font-size: 12.5px;
  color: var(--tf-fg-3);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bm-rules__body {
  padding: 20px 20px 22px;
  border-top: 1px solid var(--tf-border);
}

.bm-rules__prose {
  font-size: 17.5px;
  line-height: 2.05;
  color: var(--tf-fg-1);
  max-width: 720px;
  margin: 0;
}

.bm-inline-select {
  appearance: none;
  -webkit-appearance: none;
  border: none;
  border-bottom: 2px solid var(--tf-primary);
  background: transparent;
  color: var(--tf-primary);
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  padding: 0 4px 1px 2px;
  cursor: pointer;
  border-radius: 0;
  min-height: 32px;
}

.bm-inline-number {
  display: inline-flex;
  align-items: baseline;
  border-bottom: 2px solid var(--tf-primary);
  color: var(--tf-primary);
  font-weight: 500;
}

.bm-inline-number__input {
  border: none;
  outline: none;
  background: transparent;
  color: var(--tf-primary);
  font-family: inherit;
  font-size: inherit;
  font-weight: 500;
  padding: 0;
}

/* The threshold's on/off control, on its own line under the sentence. Quieter
   than the prose it modifies: it is a mode switch, not part of the reading. */
.bm-rules__aside {
  margin: 4px 0 0;
  font-size: 13.5px;
  color: var(--tf-fg-3);
}

.bm-rules__toggle-row {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dotted var(--tf-border-dotted);
}

.bm-switch {
  width: 40px;
  height: 24px;
  border-radius: 9999px;
  border: none;
  position: relative;
  flex-shrink: 0;
  background: var(--tf-border-strong);
  cursor: pointer;
  padding: 0;
  transition: background 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.bm-switch--on {
  background: var(--tf-primary);
}

.bm-switch__knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--tf-surface);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: left 200ms cubic-bezier(0.4, 0, 0.2, 1);
}

.bm-switch--on .bm-switch__knob {
  left: 19px;
}

.bm-rules__toggle-label {
  font-size: 14px;
  color: var(--tf-fg-1);
}

.bm-rules__toggle-sub {
  font-size: 12.5px;
  color: var(--tf-fg-3);
  margin-top: 3px;
  line-height: 1.5;
}

.bm-rules__explainer {
  margin-top: 16px;
  font-size: 12.5px;
  color: var(--tf-fg-quiet);
  line-height: 1.6;
  display: flex;
  gap: 8px;
}

.bm-rules__info {
  font-size: 16px;
  margin-top: 1px;
  color: var(--tf-fg-4);
  flex-shrink: 0;
}

/* Mobile is a primary path: 390px, single column, 44px targets. */
@media (max-width: 599px) {
  .bm-rules__header {
    padding: 14px 16px;
  }

  .bm-rules__summary {
    white-space: normal;
  }

  .bm-rules__body {
    padding: 16px;
  }

  .bm-rules__prose {
    font-size: 16px;
  }

  .bm-inline-select,
  .bm-inline-number__input {
    min-height: 44px;
  }
}
</style>
